import React from 'react';
import { Users, TrendingUp, Clock, Target, Building, MessageSquare, BarChart3 } from 'lucide-react';
import { DURATION_OPTIONS, BRAND_VIBE_OPTIONS, CLIENT_ACQUISITION_OPTIONS, CLIENT_COUNT_OPTIONS, LONG_TERM_OPTIONS, MARKETING_TIME_OPTIONS } from '../../constants/survey';

interface SurveyResponse {
  id: string;
  user_id?: string;
  name?: string;
  linkedin_profile: string;
  linkedin_full_url?: string;
  duration: string;
  brand_vibe: string;
  client_acquisition: string;
  client_count: string;
  long_term: string;
  marketing_time: string;
  additional_notes: string;
  status: string;
  created_at: string;
}

interface SurveyDashboardProps {
  responses: SurveyResponse[];
}

const SurveyDashboard: React.FC<SurveyDashboardProps> = ({ responses }) => {
  // Filter submitted responses (includes submitted, completed, and completed by user)
  const submittedResponses = responses.filter(r => 
    r.status === 'submitted' || r.status === 'completed' || r.status === 'completed by user'
  );

  // Status counts
  const statusCounts = responses.reduce((acc, response) => {
    acc[response.status] = (acc[response.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Chart data for submitted responses
  const getChartData = (field: keyof SurveyResponse, options: Record<string, string>) => {
    const counts = submittedResponses.reduce((acc, response) => {
      const value = response[field] as string;
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(options).map(([key, label]) => ({
      key,
      label,
      count: counts[key] || 0,
      percentage: submittedResponses.length > 0 ? ((counts[key] || 0) / submittedResponses.length * 100).toFixed(1) : '0'
    }));
  };

  const durationData = getChartData('duration', DURATION_OPTIONS);
  const brandVibeData = getChartData('brand_vibe', BRAND_VIBE_OPTIONS);
  const clientAcquisitionData = getChartData('client_acquisition', CLIENT_ACQUISITION_OPTIONS);
  const clientCountData = getChartData('client_count', CLIENT_COUNT_OPTIONS);
  const longTermData = getChartData('long_term', LONG_TERM_OPTIONS);
  const marketingTimeData = getChartData('marketing_time', MARKETING_TIME_OPTIONS);

  const ChartCard: React.FC<{
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    data: Array<{ key: string; label: string; count: number; percentage: string }>;
    color: string;
  }> = ({ title, icon: Icon, data, color }) => {
    const maxCount = Math.max(...data.map(d => d.count));
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-4">
          <Icon className={`w-5 h-5 mr-2 ${color}`} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        </div>
        <div className="space-y-3">
          {data.map((item) => (
            <div key={item.key} className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {item.label}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {item.count} ({item.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${color.replace('text-', 'bg-')}`}
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Status Summary Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-4">
          <BarChart3 className="w-5 h-5 mr-2 text-emerald-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Status Summary</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(statusCounts).map(([status, count]) => {
            const getStatusColor = (status: string) => {
              switch (status.toLowerCase()) {
                case 'new':
                  return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';
                case 'submitted':
                  return 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
                case 'completed':
                  return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800';
                case 'completed by user':
                  return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
                case 'closed':
                  return 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
                default:
                  return 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
              }
            };

            return (
              <div
                key={status}
                className={`p-4 rounded-lg border ${getStatusColor(status)}`}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-sm font-medium capitalize">{status}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 text-center">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Total Responses: <span className="font-semibold">{responses.length}</span> | 
            Submitted: <span className="font-semibold">{submittedResponses.length}</span>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      {submittedResponses.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard
            title="Experience Duration"
            icon={Clock}
            data={durationData}
            color="text-emerald-500"
          />
          <ChartCard
            title="Brand Vibe"
            icon={Building}
            data={brandVibeData}
            color="text-emerald-500"
          />
          <ChartCard
            title="Client Acquisition"
            icon={Target}
            data={clientAcquisitionData}
            color="text-emerald-500"
          />
          <ChartCard
            title="Client Count"
            icon={Users}
            data={clientCountData}
            color="text-emerald-500"
          />
          <ChartCard
            title="Long Term Perspective"
            icon={TrendingUp}
            data={longTermData}
            color="text-emerald-500"
          />
          <ChartCard
            title="Marketing Time Approach"
            icon={MessageSquare}
            data={marketingTimeData}
            color="text-emerald-500"
          />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <BarChart3 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Submitted Surveys Yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Charts will appear here once you have submitted survey responses.
          </p>
        </div>
      )}
    </div>
  );
};

export default SurveyDashboard;